import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, Search, Check, X, Trash2, Sparkles, BadgeCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const abs = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
const STATUSES = ["all", "pending", "approved", "rejected", "promoted"];
const ASPECTS = ["1:1", "4:5", "9:16", "16:9"];
const badge = { pending: "bg-[#FFC53D]/15 text-[#FFC53D]", approved: "bg-[#22c55e]/15 text-[#22c55e]", rejected: "bg-red-500/15 text-red-400", promoted: "bg-[#00F0FF]/15 text-[#00F0FF]" };

export default function InstagramUserUploads() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("newest");
  const [tool, setTool] = useState("all");
  const [review, setReview] = useState(null);      // detailed upload being reviewed
  const [promote, setPromote] = useState(null);     // upload being promoted
  const [cats, setCats] = useState([]);
  const [pform, setPform] = useState({ name: "", category: "", description: "", tags: "", recommended_aspect: "1:1", enabled: true });
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [bulkPromoting, setBulkPromoting] = useState(false);
  const [selectAllMatching, setSelectAllMatching] = useState(false);

  const reqId = useRef(0);
  const load = useCallback(async () => {
    const my = ++reqId.current; // ignore stale responses when filters change quickly
    setLoading(true);
    try {
      const d = await api.igAdminUploads({ status, q, sort, tool: tool === "all" ? undefined : tool, limit: 60 });
      if (my !== reqId.current) return;
      setItems(d.items || []); setTotal(d.total || 0); setSelected(new Set()); setSelectAllMatching(false);
    } catch { if (my === reqId.current) toast.error("Could not load user uploads."); }
    if (my === reqId.current) setLoading(false);
  }, [status, q, sort, tool]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.igAdminCategories().then(setCats).catch(() => {}); }, []);

  const openReview = async (id) => { try { setReview(await api.igAdminUploadDetail(id)); } catch { toast.error("Failed to load."); } };
  const setStatusFor = async (id, s) => {
    try { const d = await api.igAdminUpdateUpload(id, s); setItems((x) => x.map((v) => (v.id === id ? { ...v, review_status: d.review_status } : v))); if (review?.id === id) setReview({ ...review, review_status: d.review_status }); toast.success(`Marked ${s}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this upload record?")) return; try { await api.igAdminDeleteUpload(id); setItems((x) => x.filter((v) => v.id !== id)); setReview(null); setTotal((t) => Math.max(0, t - 1)); toast.success("Deleted"); } catch { toast.error("Failed"); } };

  // ---- bulk selection ----
  const toggleSel = (id) => { setSelectAllMatching(false); setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const allVisibleSelected = items.length > 0 && items.every((u) => selected.has(u.id));
  const toggleSelectAll = () => { setSelectAllMatching(false); setSelected((s) => (allVisibleSelected ? new Set() : new Set(items.map((u) => u.id)))); };
  const clearSelection = () => { setSelected(new Set()); setSelectAllMatching(false); };
  const selectedCount = selectAllMatching ? total : selected.size;
  const bulkOpts = selectAllMatching ? { all_matching: true, status, q, tool: tool === "all" ? undefined : tool } : {};
  const bulkIds = selectAllMatching ? [] : Array.from(selected);

  const doBulkDelete = async () => {
    if (deleting || selectedCount === 0) return;
    setDeleting(true);
    try {
      const res = await api.igBulkDeleteUploads(bulkIds, bulkOpts);
      setConfirmOpen(false);
      if (res.failed > 0 || res.skipped > 0) toast.warning(`${res.deleted} deleted, ${res.failed} failed, ${res.skipped} skipped.`);
      else toast.success(`${res.deleted} user upload${res.deleted === 1 ? "" : "s"} deleted successfully.`);
      const preserved = (res.details || []).filter((d) => d.asset_preserved).length;
      if (preserved > 0) toast.message(`${preserved} promoted template asset${preserved === 1 ? "" : "s"} preserved.`);
      if (selectAllMatching) { await load(); }
      else {
        const deletedIds = new Set((res.details || []).filter((d) => d.result === "deleted").map((d) => d.id));
        setItems((x) => x.filter((v) => !deletedIds.has(v.id)));
        setTotal((t) => Math.max(0, t - (res.deleted || 0)));
        setSelected((s) => { const n = new Set(s); deletedIds.forEach((id) => n.delete(id)); return n; });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk delete failed."); }
    setDeleting(false);
  };

  const doBulkReview = async (reviewStatus) => {
    if (selectedCount === 0) return;
    try {
      const res = await api.igBulkReviewUploads(bulkIds, reviewStatus, bulkOpts);
      toast.success(`${res.updated} marked ${reviewStatus}.`);
      if (selectAllMatching) await load();
      else setItems((x) => x.map((v) => (selected.has(v.id) && v.review_status !== "promoted" ? { ...v, review_status: reviewStatus } : v)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk review failed."); }
  };

  const doBulkPromote = async () => {
    if (bulkPromoting || selectedCount === 0) return;
    setBulkPromoting(true);
    try {
      const res = await api.igBulkPromoteUploads(bulkIds, undefined, bulkOpts);
      if (res.failed > 0 || res.skipped > 0) toast.warning(`${res.promoted} promoted, ${res.skipped} skipped, ${res.failed} failed.`);
      else toast.success(`${res.promoted} promoted to templates.`);
      if (selectAllMatching) { await load(); }
      else {
        const promotedIds = new Set((res.details || []).filter((d) => d.result === "promoted").map((d) => d.id));
        setItems((x) => x.map((v) => (promotedIds.has(v.id) ? { ...v, review_status: "promoted" } : v)));
        setSelected(new Set());
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk promote failed."); }
    setBulkPromoting(false);
  };

  const openPromote = (u) => { setPromote(u); setPform({ name: u.template_name && u.template_name !== "Custom reference" ? u.template_name : "", category: u.category && u.category !== "Custom" ? u.category : (cats[0]?.name || ""), description: "", tags: "", recommended_aspect: u.aspect || "1:1", enabled: true }); };
  const doPromote = async () => {
    if (!pform.name.trim() || !pform.category) { toast.error("Name and category are required."); return; }
    setSaving(true);
    try {
      await api.igAdminPromoteUpload(promote.id, pform);
      setItems((x) => x.map((v) => (v.id === promote.id ? { ...v, review_status: "promoted" } : v)));
      toast.success("Added to template library");
      setPromote(null); setReview(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Promotion failed"); }
    setSaving(false);
  };

  return (
    <div data-testid="instagram-user-uploads">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-black text-white">Design Review Library</h2>
          <p className="text-xs text-[#A1A1AA]">Every design users generate — Instagram posts and YouTube thumbnails. Review, approve and add the best to the Instagram template gallery. ({total})</p>
        </div>
        <button onClick={load} disabled={loading} data-testid="ig-uploads-refresh" className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white disabled:opacity-50"><RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh</button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-1.5">
          <Search size={14} className="text-[#666]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="ig-uploads-search" placeholder="Search user / generation id / template…" className="w-full bg-transparent text-sm text-white outline-none placeholder:text-[#666]" />
        </div>
        <div className="flex gap-1">
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`ig-uploads-filter-${s}`} className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold capitalize ${status === s ? "bg-[#E1306C] text-white" : "border border-white/10 text-[#A1A1AA] hover:text-white"}`}>{s}</button>
          ))}
        </div>
        <div className="flex gap-1" data-testid="ig-uploads-tool-filter">
          {[["all", "All tools"], ["instagram", "Instagram"], ["youtube_thumbnail", "YouTube thumbnails"]].map(([k, l]) => (
            <button key={k} onClick={() => setTool(k)} data-testid={`ig-uploads-tool-${k}`} className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${tool === k ? "bg-white text-black" : "border border-white/10 text-[#A1A1AA] hover:text-white"}`}>{l}</button>
          ))}
        </div>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="ig-uploads-sort" className="rounded-lg border border-white/10 bg-[#0A0A0A] px-2.5 py-1.5 text-xs text-white">
          <option value="newest">Newest</option><option value="oldest">Oldest</option>
        </select>
      </div>

      {items.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
          <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-white">
            <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} data-testid="ig-uploads-select-all" className="h-4 w-4 accent-[#E1306C]" />
            Select All <span className="text-xs font-normal text-[#666]">({items.length} shown{total > items.length ? ` of ${total}` : ""})</span>
          </label>
          {selectedCount > 0 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="ig-bulk-bar">
              <span className="text-xs font-bold text-[#E1306C]" data-testid="ig-selected-count">{selectedCount} selected</span>
              {!selectAllMatching && allVisibleSelected && total > items.length && (
                <button onClick={() => setSelectAllMatching(true)} data-testid="ig-select-all-matching" className="rounded-lg border border-[#00F0FF]/40 bg-[#00F0FF]/10 px-2.5 py-1.5 text-xs font-bold text-[#00F0FF] hover:bg-[#00F0FF]/20">Select all {total} matching</button>
              )}
              {selectAllMatching && (
                <span className="rounded-lg bg-[#00F0FF]/15 px-2.5 py-1 text-[11px] font-bold text-[#00F0FF]" data-testid="ig-all-matching-note">All {total} matching selected</span>
              )}
              <button onClick={() => doBulkReview("approved")} data-testid="ig-bulk-approve-btn" className="flex items-center gap-1.5 rounded-lg bg-[#22c55e]/15 px-3 py-1.5 text-xs font-bold text-[#22c55e] hover:bg-[#22c55e]/25"><Check size={13} /> Approve</button>
              <button onClick={() => doBulkReview("rejected")} data-testid="ig-bulk-reject-btn" className="flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-xs font-bold text-[#A1A1AA] hover:bg-white/20"><X size={13} /> Reject</button>
              <button onClick={doBulkPromote} disabled={bulkPromoting} data-testid="ig-bulk-promote-btn" className="flex items-center gap-1.5 rounded-lg bg-[#8B5CF6] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#8B5CF6]/90 disabled:opacity-50"><Sparkles size={13} /> {bulkPromoting ? "Promoting…" : "Promote to Templates"}</button>
              <button onClick={() => setConfirmOpen(true)} data-testid="ig-bulk-delete-btn" className="flex items-center gap-1.5 rounded-lg bg-red-500/15 px-3 py-1.5 text-xs font-bold text-red-400 hover:bg-red-500/25"><Trash2 size={13} /> Delete Selected</button>
              <button onClick={clearSelection} data-testid="ig-clear-selection-btn" className="rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white">Clear Selection</button>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#E1306C]" /></div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-sm text-[#666]" data-testid="ig-uploads-empty">No designs here yet. Every design users generate appears here for review.</div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="ig-uploads-grid">
          {items.map((u) => (
            <div key={u.id} data-testid={`ig-upload-${u.id}`} className={`relative overflow-hidden rounded-xl border bg-white/[0.02] ${selected.has(u.id) ? "border-[#E1306C]" : "border-white/10"}`}>
              <label className="absolute left-2 top-2 z-10 flex h-6 w-6 cursor-pointer items-center justify-center rounded bg-black/60" onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggleSel(u.id)} data-testid={`ig-upload-check-${u.id}`} className="h-4 w-4 accent-[#E1306C]" />
              </label>
              <div className={`${u.tool === "youtube_thumbnail" && (u.aspect || "16:9") === "16:9" ? "aspect-video" : "aspect-square"} bg-black`}><img loading="lazy" src={abs(u.result_url)} alt="" className={`h-full w-full ${u.tool === "youtube_thumbnail" ? "object-contain" : "object-cover"}`} /></div>
              <div className="p-2.5">
                <div className="flex items-center justify-between">
                  <span className="truncate text-xs font-bold text-white">{u.user_name || "User"}</span>
                  <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold capitalize ${badge[u.review_status] || "bg-white/10 text-[#A1A1AA]"}`} data-testid={`ig-upload-status-${u.id}`}>{u.review_status}</span>
                </div>
                <div className="text-[10px] text-[#666]">
                  <span className={`mr-1 rounded px-1 py-px text-[9px] font-bold ${u.tool === "youtube_thumbnail" ? "bg-[#FF3B3B]/20 text-[#ff7a7a]" : "bg-[#E1306C]/20 text-[#f48fb1]"}`} data-testid={`ig-upload-tool-${u.id}`}>{u.tool === "youtube_thumbnail" ? "YouTube" : "Instagram"}</span>
                  {u.category} · {u.aspect}{u.tool === "youtube_thumbnail" ? "" : ` · ${u.template_source === "user_reference" ? "own ref" : u.template_source === "none" ? "AI" : "our template"}`}
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  <button onClick={() => openReview(u.id)} data-testid={`ig-upload-review-${u.id}`} className="rounded-lg bg-[#E1306C] px-2 py-1 text-[11px] font-bold text-white">Review</button>
                  {u.review_status === "promoted" ? (
                    <span className="flex items-center gap-1 rounded-lg bg-[#00F0FF]/15 px-2 py-1 text-[11px] font-bold text-[#00F0FF]"><BadgeCheck size={12} /> Promoted</span>
                  ) : (
                    <button onClick={() => openPromote(u)} data-testid={`ig-upload-promote-${u.id}`} className="flex items-center gap-1 rounded-lg border border-[#8B5CF6]/40 px-2 py-1 text-[11px] font-semibold text-[#a78bfa]"><Sparkles size={12} /> Add to Templates</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Review modal */}
      <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent className="max-w-3xl border-white/10 bg-[#0A0A0A]" data-testid="ig-review-modal">
          <DialogTitle className="text-white">Review generated post</DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">Approve, reject or add this to the template library.</DialogDescription>
          {review && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <div className="mb-1 text-[10px] uppercase tracking-wide text-[#666]">Generated image</div>
                <img src={abs(review.result_url)} alt="" className="w-full rounded-lg border border-white/10" data-testid="ig-review-image" />
              </div>
              <div className="space-y-2 text-sm">
                {review.template_ref_url && (
                  <div>
                    <div className="mb-1 text-[10px] uppercase tracking-wide text-[#666]">Template reference</div>
                    <img src={abs(review.template_ref_url)} alt="" className="w-32 rounded-lg border border-white/10" />
                  </div>
                )}
                <div className="text-[#A1A1AA]">User: <span className="text-white">{review.user_name || "—"}</span></div>
                <div className="text-[#A1A1AA]">Category: <span className="text-white">{review.category}</span></div>
                <div className="text-[#A1A1AA]">Source: <span className="text-white">{review.template_source === "user_reference" ? "User reference" : "Our template"}</span></div>
                <div className="text-[#A1A1AA]">Aspect: <span className="text-white">{review.aspect}</span></div>
                <div className="text-[#A1A1AA]">Status: <span className="capitalize text-white">{review.review_status}</span></div>
                {review.prompt_summary && <div className="text-[#A1A1AA]">Prompt: <span className="text-white">{review.prompt_summary}</span></div>}
                <div className="flex flex-wrap gap-2 pt-2">
                  <button onClick={() => setStatusFor(review.id, "approved")} data-testid="ig-review-approve" className="flex items-center gap-1 rounded-lg bg-[#22c55e]/15 px-3 py-1.5 text-xs font-bold text-[#22c55e]"><Check size={13} /> Approve</button>
                  <button onClick={() => setStatusFor(review.id, "rejected")} data-testid="ig-review-reject" className="flex items-center gap-1 rounded-lg bg-red-500/15 px-3 py-1.5 text-xs font-bold text-red-400"><X size={13} /> Reject</button>
                  {review.review_status !== "promoted" && <button onClick={() => openPromote(review)} data-testid="ig-review-promote" className="flex items-center gap-1 rounded-lg bg-[#8B5CF6] px-3 py-1.5 text-xs font-bold text-white"><Sparkles size={13} /> Add to Templates</button>}
                  <button onClick={() => del(review.id)} data-testid="ig-review-delete" className="flex items-center gap-1 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-red-400"><Trash2 size={13} /> Delete</button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Promote modal */}
      <Dialog open={!!promote} onOpenChange={(o) => !o && setPromote(null)}>
        <DialogContent className="max-w-md border-white/10 bg-[#0A0A0A]" data-testid="ig-promote-modal">
          <DialogTitle className="text-white">Add to Instagram Templates</DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">Publish this post as a reusable template.</DialogDescription>
          <div className="space-y-2">
            <input value={pform.name} onChange={(e) => setPform({ ...pform, name: e.target.value })} placeholder="Template name" data-testid="ig-promote-name" className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none" />
            <select value={pform.category} onChange={(e) => setPform({ ...pform, category: e.target.value })} data-testid="ig-promote-category" className="w-full rounded-lg border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm text-white">
              <option value="">Select category…</option>
              {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
            <input value={pform.description} onChange={(e) => setPform({ ...pform, description: e.target.value })} placeholder="Description (optional)" className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none" />
            <input value={pform.tags} onChange={(e) => setPform({ ...pform, tags: e.target.value })} placeholder="tags, comma, separated" className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none" />
            <select value={pform.recommended_aspect} onChange={(e) => setPform({ ...pform, recommended_aspect: e.target.value })} className="w-full rounded-lg border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm text-white">
              {ASPECTS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setPromote(null)} className="rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#A1A1AA]">Cancel</button>
              <button onClick={doPromote} disabled={saving} data-testid="ig-promote-submit" className="flex items-center gap-1.5 rounded-lg bg-[#E1306C] px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">{saving ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Add Template</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk delete confirmation */}
      <Dialog open={confirmOpen} onOpenChange={(o) => !deleting && setConfirmOpen(o)}>
        <DialogContent className="max-w-md border-white/10 bg-[#0A0A0A]" data-testid="ig-bulk-confirm-modal">
          <DialogTitle className="text-white">Delete selected uploads?</DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">
            You are about to permanently delete {selectedCount} user upload{selectedCount === 1 ? "" : "s"}. This action cannot be undone. Promoted template images stay safe.
          </DialogDescription>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setConfirmOpen(false)} disabled={deleting} className="rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#A1A1AA] disabled:opacity-50">Cancel</button>
            <button onClick={doBulkDelete} disabled={deleting} data-testid="ig-bulk-confirm-btn" className="flex items-center gap-1.5 rounded-lg bg-red-500 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60">
              {deleting ? <><Loader2 size={13} className="animate-spin" /> Deleting…</> : <><Trash2 size={13} /> Delete {selectedCount} Item{selectedCount === 1 ? "" : "s"}</>}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
